import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageCircle, Loader2, AlertCircle } from "lucide-react";
import { useChateaux } from "@/hooks/useChateaux";
import { useReservationForm } from "@/hooks/useReservationForm";
import { ProductSelector } from "@/components/form/ProductSelector";
import { FormFields } from "@/components/form/FormFields";

interface ReservationFormProps {
  preSelectedChateau?: string;
  initialDateDebut?: Date;
  initialDateFin?: Date;
  onSuccess?: () => void;
}

export const ReservationForm = ({ preSelectedChateau, initialDateDebut, initialDateFin, onSuccess }: ReservationFormProps) => {
  const { chateaux } = useChateaux();
  
  const {
    form,
    isLoading,
    showSuccess,
    showChateauType,
    handleProductChange,
    onSubmit,
    chateauOptions,
    dateDebut,
    dateFin,
    setDateDebut,
    setDateFin,
    hasDatesSelected,
    availabilityLoading,
    isSumoAvailable,
  } = useReservationForm({
    preSelectedChateau,
    chateaux,
    onSuccess,
    initialDateDebut,
    initialDateFin
  });

  const sumoUnavailable = hasDatesSelected && !isSumoAvailable();
  const wantsChateau = form.watch("products.chateau");
  const wantsSumo = form.watch("products.sumo");
  const selectedChateauType = form.watch("chateauType");
  const chateauBlock = wantsChateau && (chateauOptions.length === 0 || !selectedChateauType);
  const sumoBlock = wantsSumo && sumoUnavailable;
  const submitBlocked = chateauBlock || sumoBlock;

  return (
    <Card className="shadow-card border-0">
      <CardHeader>
        <CardTitle className="text-2xl font-display">Demande de Réservation</CardTitle>
        <p className="text-muted-foreground">
          Remplissez ce formulaire et nous vous recontactons rapidement pour finaliser votre réservation.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormFields 
            form={form}
            dateDebut={dateDebut}
            dateFin={dateFin}
            onDateDebutChange={setDateDebut}
            onDateFinChange={setDateFin}
          />

          {!hasDatesSelected && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 border border-amber-200">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">Veuillez d'abord sélectionner vos dates pour voir les produits disponibles.</p>
            </div>
          )}
          
          {hasDatesSelected && (
            <ProductSelector
              form={form}
              showChateauType={showChateauType}
              onProductChange={handleProductChange}
              chateauOptions={chateauOptions}
              sumoUnavailable={sumoUnavailable}
            />
          )}

          <div className="flex justify-center">
            <Button 
              type="submit" 
              variant="hero" 
              size="lg" 
              className="w-full max-w-md"
              disabled={isLoading || !hasDatesSelected || availabilityLoading || submitBlocked}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <MessageCircle className="w-4 h-4 mr-2" />
              )}
              {isLoading ? "Envoi en cours..." : "Envoyer ma Demande de Réservation"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
